import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Paperclip, History, Eye, Trash2, UploadCloud, FileText, FileUp, X, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { fdate, fdatetime, eur2, CvStatus, CV_STATUS_META } from "@/lib/format";
import ContraventionPdfImport, { DriverPicker } from "@/components/common/ContraventionPdfImport";

const ALL = "all";
const STATUS_OPTIONS = Object.entries(CV_STATUS_META).map(([value, m]) => ({ value, label: m.label }));

export const CvBadge = ({ status }) => {
  const m = CvStatus(status);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${m.cls}`} data-testid={`cv-status-${status}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.dot }} />{m.label}
    </span>
  );
};

const EMPTY = {
  category: "infraction", vehicle_id: "", employee_id: "", infraction_date: "", infraction_time: "", location: "",
  infraction_type: "", notice_number: "", initial_amount: "", points: "",
  received_date: "", payment_deadline: "", designation_deadline: "", comments: "", status: "nouvelle",
};

function ContraventionForm({ open, onOpenChange, editing, vehicles, employees, scope, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing } : { ...EMPTY, ...scope });
  }, [open, editing, scope]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["initial_amount", "points"].forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
      if (editing) await api.put(`/contraventions/${editing.id}`, payload);
      else await api.post("/contraventions", payload);
      toast.success(editing ? "Contravention modifiée" : "Contravention créée");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const F = ({ k, label, type = "text", full }) => (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">{label}</Label>
      <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`cv-field-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier" : "Nouvelle"} contravention</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">Catégorie</Label>
            <Select value={form.category || "infraction"} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="cv-field-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="stationnement">Stationnement (FPS)</SelectItem><SelectItem value="infraction">Infraction</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">Véhicule</Label>
            <Select value={form.vehicle_id || ""} onValueChange={(v) => set("vehicle_id", v)}>
              <SelectTrigger data-testid="cv-field-vehicle_id"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.registration})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">Conducteur / salarié</Label>
            <Select value={form.employee_id || ""} onValueChange={(v) => set("employee_id", v)}>
              <SelectTrigger data-testid="cv-field-employee_id"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <F k="notice_number" label="N° de l'avis de contravention" />
          <F k="infraction_type" label="Type d'infraction" />
          <F k="infraction_date" label="Date de l'infraction" type="date" />
          <F k="infraction_time" label="Heure de l'infraction" type="time" />
          <F k="location" label="Lieu de l'infraction" full />
          <F k="initial_amount" label="Montant initial (€)" type="number" />
          <F k="points" label="Points concernés" type="number" />
          <F k="received_date" label="Date de réception" type="date" />
          <F k="payment_deadline" label="Date limite de paiement" type="date" />
          <F k="designation_deadline" label="Date limite de désignation" type="date" />
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">Statut</Label>
            <Select value={form.status || "nouvelle"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="cv-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">Commentaires</Label>
            <Textarea value={form.comments || ""} onChange={(e) => set("comments", e.target.value)} data-testid="cv-field-comments" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="cv-save-btn">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContraventionDetail({ id, open, onOpenChange, canWrite, onChanged }) {
  const [c, setC] = useState(null);
  const [status, setStatus] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [docType, setDocType] = useState("avis");
  const [busy, setBusy] = useState(false);
  const [emps, setEmps] = useState([]);
  const [designateId, setDesignateId] = useState("");

  const load = useCallback(() => {
    if (!id) return;
    api.get(`/contraventions/${id}`).then((r) => { setC(r.data); setStatus(r.data.status); setDesignateId(r.data.employee_id || ""); setPaidAmount(r.data.paid_amount ?? r.data.initial_amount ?? ""); }).catch((e) => toast.error(apiError(e)));
  }, [id]);
  useEffect(() => { if (open) { load(); api.get("/employees").then((r) => setEmps(r.data)).catch(() => {}); } }, [open, load]);

  const saveStatus = async () => {
    try {
      const body = { status };
      if (status === "payee" && paidAmount !== "") body.paid_amount = Number(paidAmount);
      await api.post(`/contraventions/${id}/status`, body); toast.success("Statut mis à jour"); load(); onChanged && onChanged();
    }
    catch (e) { toast.error(apiError(e)); }
  };
  const designate = async () => {
    try { await api.post(`/contraventions/${id}/designate`, { employee_id: designateId }); toast.success("Conducteur désigné"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", docType);
    try { await api.post(`/contraventions/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document ajouté"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const Info = ({ label, value }) => (<div><dt className="text-xs uppercase tracking-wide text-[#8695AB]">{label}</dt><dd className="mt-0.5 font-medium text-[#18221F]">{value || "—"}</dd></div>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-3">Contravention {c?.notice_number || ""} {c && <CvBadge status={c.status} />}</DialogTitle></DialogHeader>
        {!c ? <Skeleton className="h-64 w-full" /> : (
          <Tabs defaultValue="infos">
            <TabsList className="mb-3"><TabsTrigger value="infos" data-testid="cv-tab-infos">Informations</TabsTrigger><TabsTrigger value="docs" data-testid="cv-tab-docs">Documents</TabsTrigger><TabsTrigger value="hist" data-testid="cv-tab-hist">Historique</TabsTrigger></TabsList>
            <TabsContent value="infos">
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <Info label="Véhicule" value={c.vehicle_label} /><Info label="Immatriculation" value={c.registration} /><Info label="Conducteur" value={c.employee_label} />
                <Info label="Date" value={fdate(c.infraction_date)} /><Info label="Heure" value={c.infraction_time} /><Info label="Lieu" value={c.location} />
                <Info label="Catégorie" value={c.category === "stationnement" ? "Stationnement (FPS)" : "Infraction"} /><Info label="Type" value={c.infraction_type} /><Info label="Montant initial" value={c.initial_amount != null ? eur2(c.initial_amount) : "—"} /><Info label="Points" value={c.points} />
                <Info label="Réception" value={fdate(c.received_date)} /><Info label="Limite paiement" value={fdate(c.payment_deadline)} /><Info label="Limite désignation" value={fdate(c.designation_deadline)} />
              </dl>
              {c.comments && <div className="mt-4 rounded-lg bg-[#F5F7FA] p-3 text-sm text-[#4A5568]">{c.comments}</div>}
              {canWrite && (
                <div className="mt-4 flex flex-wrap items-end gap-2 rounded-lg border border-[#E2E8F0] p-3">
                  <div className="min-w-[220px] flex-1"><Label className="mb-1.5 block text-xs text-[#4A5568]">Désigner / modifier le conducteur</Label>
                    <DriverPicker employees={emps} value={designateId} onChange={setDesignateId} testid="cv-detail-driver" /></div>
                  <Button onClick={designate} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="cv-designate-save">Désigner</Button>
                </div>
              )}
              {canWrite && (
                <div className="mt-5 flex flex-wrap items-end gap-2 rounded-lg border border-[#E2E8F0] p-3">
                  <div className="flex-1 min-w-[180px]"><Label className="mb-1.5 block text-xs text-[#4A5568]">Changer le statut</Label>
                    <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="cv-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
                  </div>
                  {status === "payee" && (
                    <div className="w-40"><Label className="mb-1.5 block text-xs text-[#4A5568]">Montant payé (€)</Label>
                      <Input type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} data-testid="cv-paid-amount" /></div>
                  )}
                  <Button onClick={saveStatus} className="bg-[#001A3B]" data-testid="cv-status-save">Appliquer</Button>
                </div>
              )}
            </TabsContent>
            <TabsContent value="docs">
              {canWrite && (
                <div className="mb-4 flex items-end gap-2 rounded-lg border border-dashed border-[#E2E8F0] p-3">
                  <div><Label className="mb-1.5 block text-xs text-[#4A5568]">Type de document</Label>
                    <Select value={docType} onValueChange={setDocType}><SelectTrigger className="w-52" data-testid="cv-doc-type"><SelectValue /></SelectTrigger><SelectContent>
                      {[["avis", "Avis de contravention"], ["designation", "Justificatif de désignation"], ["paiement", "Justificatif de paiement"], ["contestation", "Document de contestation"], ["autre", "Autre document"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent></Select>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#A5894E] px-3 py-2 text-sm font-medium text-white hover:bg-[#8F7440]" data-testid="cv-doc-upload">
                    <UploadCloud className="h-4 w-4" />{busy ? "Envoi…" : "Ajouter (PDF/Image)"}
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                  </label>
                </div>
              )}
              {(!c.documents || c.documents.length === 0) ? <p className="py-8 text-center text-sm text-[#8695AB]">Aucun document</p> : (
                <div className="space-y-2">{c.documents.map((d) => (
                  <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border border-[#EDF0F5] px-3 py-2 hover:bg-[#F5F7FA]" data-testid="cv-doc-row">
                    <FileText className="h-4 w-4 text-[#001A3B]" /><span className="flex-1 truncate text-sm">{d.original_filename}</span>
                    <span className="rounded-full bg-[#EEF2F8] px-2 py-0.5 text-[11px] text-[#001A3B]">{d.doc_type || d.category}</span>
                    <span className="text-xs text-[#8695AB]">{fdate(d.created_at)}</span>
                  </a>
                ))}</div>
              )}
            </TabsContent>
            <TabsContent value="hist">
              {(!c.history || c.history.length === 0) ? <p className="py-8 text-center text-sm text-[#8695AB]">Aucun historique</p> : (
                <div className="space-y-3">{[...c.history].reverse().map((h, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#A5894E]" />
                    <div><p className="text-sm text-[#18221F]">{h.action}</p><p className="text-xs text-[#8695AB]">{fdatetime(h.date)} · {h.user_name || "Système"}</p></div>
                  </div>
                ))}</div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ContraventionsView({ scope = {}, showFilters = true, useUrl = false }) {
  const { user } = useAuth();
  const canWrite = ["admin", "fleet_manager"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const up = (k) => (useUrl ? params.get(k) : null);
  const [rows, setRows] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState(up("statut") || ALL);
  const [fVehicle, setFVehicle] = useState(up("vehicule") || ALL);
  const fEmployee = up("conducteur");
  const fCompany = up("entite");
  const fStart = up("du");
  const fEnd = up("au");
  const fCategory = up("categorie");
  useEffect(() => {
    if (!useUrl) return;
    setFStatus(params.get("statut") || ALL);
    setFVehicle(params.get("vehicule") || ALL);
  }, [params, useUrl]);
  const clearParam = (k) => { const p = new URLSearchParams(params); p.delete(k); setParams(p); };
  const [formOpen, setFormOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (scope.vehicle_id) p.set("vehicle_id", scope.vehicle_id);
    if (scope.employee_id) p.set("employee_id", scope.employee_id);
    if (fStatus !== ALL) p.set("status", fStatus);
    if (fVehicle !== ALL) p.set("vehicle_id", fVehicle);
    if (fEmployee) p.set("employee_id", fEmployee);
    if (fCompany) p.set("company", fCompany);
    if (fStart) p.set("start", fStart);
    if (fEnd) p.set("end", fEnd);
    if (fCategory) p.set("category", fCategory);
    if (search) p.set("q", search);
    api.get(`/contraventions?${p.toString()}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [scope.vehicle_id, scope.employee_id, fStatus, fVehicle, search, fEmployee, fCompany, fStart, fEnd, fCategory]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const remove = async (row) => { try { await api.delete(`/contraventions/${row.id}`); toast.success("Supprimée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const openDetail = (row) => { setDetailId(row.id); setDetailOpen(true); };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8695AB]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Immat, salarié, n° avis…" className="bg-white pl-9" data-testid="cv-search" />
        </div>
        {showFilters && (
          <>
            <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-48 bg-white" data-testid="cv-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tous les statuts</SelectItem><SelectItem value="open">Ouvertes (non clôturées)</SelectItem>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
            <Select value={fVehicle} onValueChange={setFVehicle}><SelectTrigger className="w-56 bg-white" data-testid="cv-filter-vehicle"><SelectValue placeholder="Véhicule" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tous les véhicules</SelectItem>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.registration})</SelectItem>)}</SelectContent></Select>
          </>
        )}
        {[["conducteur", fEmployee && `Conducteur : ${(employees.find((e) => e.id === fEmployee) || {}).first_name || ""} ${(employees.find((e) => e.id === fEmployee) || {}).last_name || ""}`],
          ["entite", fCompany && `Entité : ${fCompany}`], ["du", fStart && `Du ${fdate(fStart)}`], ["au", fEnd && `Au ${fdate(fEnd)}`],
          ["categorie", fCategory && (fCategory === "stationnement" ? "Stationnement (FPS)" : "Infraction")]].filter(([, l]) => l).map(([k, l]) => (
          <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-[#A5894E]/40 bg-[#F4EFE3] px-3 py-1 text-xs font-medium text-[#6b5626]" data-testid={`cv-url-filter-${k}`}>
            <Filter className="h-3 w-3" />{l}
            <button onClick={() => clearParam(k)} className="rounded-full p-0.5 hover:bg-[#A5894E]/20" data-testid={`cv-url-filter-clear-${k}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {rows && <span className="text-sm text-[#8695AB]">{rows.length} élément(s)</span>}
          {canWrite && showFilters && <Button onClick={() => setPdfOpen(true)} variant="outline" className="border-[#001A3B] text-[#001A3B]" data-testid="cv-import-pdf-btn"><FileUp className="mr-2 h-4 w-4" />Importer une contravention PDF</Button>}
          {canWrite && <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="cv-add-btn"><Plus className="mr-2 h-4 w-4" />Nouvelle contravention</Button>}
        </div>
      </div>

      {rows === null ? <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
        : rows.length === 0 ? <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-white/60 p-10 text-center text-sm text-[#4A5568]">Aucune contravention.</div>
        : (
          <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left text-xs uppercase tracking-wide text-[#4A5568]">{["Date", "Immat.", "Type", "Conducteur", "Montant", "Éch. paiement", "Statut", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{rows.map((c) => (
              <tr key={c.id} className="cursor-pointer border-b border-[#EDF0F5] last:border-0 hover:bg-[#F5F7FA]/60" onClick={() => openDetail(c)} data-testid="cv-row">
                <td className="px-4 py-3">{fdate(c.infraction_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.registration || "—"}</td>
                <td className="px-4 py-3">{c.infraction_type || "—"}</td>
                <td className="px-4 py-3">{c.employee_label || "—"}</td>
                <td className="px-4 py-3">{c.initial_amount != null ? eur2(c.initial_amount) : "—"}</td>
                <td className="px-4 py-3">{fdate(c.payment_deadline)}</td>
                <td className="px-4 py-3"><CvBadge status={c.status} /></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openDetail(c)} className="rounded-lg p-1.5 text-[#4A5568] hover:bg-[#EEF2F8] hover:text-[#001A3B]" data-testid="cv-view-btn"><Eye className="h-4 w-4" /></button>
                    {canWrite && <button onClick={() => { setEditing(c); setFormOpen(true); }} className="rounded-lg p-1.5 text-[#4A5568] hover:bg-[#EEF2F8] hover:text-[#001A3B]" data-testid="cv-edit-btn"><History className="h-4 w-4" /></button>}
                    {canWrite && (
                      <AlertDialog><AlertDialogTrigger asChild><button className="rounded-lg p-1.5 text-[#4A5568] hover:bg-red-50 hover:text-red-600" data-testid="cv-delete-btn"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Supprimer cette contravention ?</AlertDialogTitle><AlertDialogDescription>Action irréversible.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => remove(c)} className="bg-red-600" data-testid="cv-confirm-delete">Supprimer</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div></div>
        )}

      <ContraventionForm open={formOpen} onOpenChange={setFormOpen} editing={editing} vehicles={vehicles} employees={employees} scope={scope} onSaved={load} />
      <ContraventionDetail id={detailId} open={detailOpen} onOpenChange={setDetailOpen} canWrite={canWrite} onChanged={load} />
      <ContraventionPdfImport open={pdfOpen} onOpenChange={setPdfOpen} onDone={load} />
    </div>
  );
}
